import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { ImageSourcePropType, TextStyle } from 'react-native';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import { theme, TYPE_CONFIG } from '@garnish/constants';

import { Image } from '../Image';
import { BodyText } from '../Text';
import { Card } from '../Card';
import { useResponsive } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const RewardCard = (props: RewardCardProps) => {
  const {
    id,
    description,
    isSelected,
    isDisabled,
    extraDetails,
    imageSource,
    palette = 'quinoa',
    onPress,
    ...restProps
  } = props;

  const { match } = useResponsive();
  const { fontScale } = useWindowDimensions();

  // ─── Styles ──────────────────────────────────────────────────────────

  const descriptionStyles = [
    [palettePresetsForText[palette].description],
    match([undefined, styles.descriptionSM]),
  ];
  const extraDetailsStyles = [
    styles.extraDetails,
    palettePresetsForText[palette].extraDetails,
  ];

  // ─────────────────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.(id);
  }, [id, onPress]);

  const onPressHandler = onPress ? handleOnPress : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Card
      testID={`sg-add-on-card-${id}`}
      style={styles.cardContainer}
      accessibilityLabel={description}
      accessibilityRole="button"
      onPress={onPressHandler}
      isSelected={isSelected}
      disabled={isDisabled}
      {...palettePresetsForCard[palette]}
      {...restProps}
    >
      <View style={styles.cardInner}>
        <Image
          source={imageSource}
          resizeMode="cover"
          accessibilityIgnoresInvertColors
          style={styles.illustration}
        />

        <View style={styles.infoContainer}>
          <View style={styles.descriptionWrapper}>
            <BodyText
              numberOfLines={fontScale > 1.1 ? 2 : 3}
              ellipsizeMode="tail"
              size={3}
              style={descriptionStyles}
            >
              {description}
            </BodyText>
          </View>

          {extraDetails ? (
            <BodyText
              testID="sg-reward-card-deadline"
              size={5}
              style={extraDetailsStyles}
            >
              {extraDetails}
            </BodyText>
          ) : null}
        </View>
      </View>
    </Card>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardContainer: {
    minHeight: 120,
    paddingHorizontal: theme.spacing['4'],
    borderRadius: theme.radius.large,
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardInner: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  infoContainer: {
    flex: 1,
    paddingLeft: theme.spacing['4'],
  },
  descriptionWrapper: {
    paddingBottom: theme.spacing['2'],
  },
  descriptionSM: {
    // We override fluid text styles because we need static 16px styles starting from
    // the `SM` breakpoint, which the present fluid text styles cannot provide.
    ...TYPE_CONFIG.BODY['16'],
  },
  extraDetails: {
    textTransform: 'uppercase',
  },
  illustration: {
    width: 80,
    height: 80,
  },
});

// ─── Palettes ────────────────────────────────────────────────────────────────

const palettePresetsForCard: Record<RewardCardPalette, CardStyleProps> = {
  quinoa: {
    backgroundColor: theme.colors.QUINOA,
    backgroundColorHover: theme.colors.QUINOA_HOVER,
    selectedBackgroundColor: theme.colors.CUCUMBER,
    selectedBackgroundColorHover: theme.colors.CUCUMBER_HOVER,
    selectedBorderColor: theme.colors.SPINACH,
  },
  kale: {
    backgroundColor: theme.colors.KALE,
    backgroundColorHover: theme.colors.KALE_HOVER,
    selectedBackgroundColor: theme.colors.KALE,
    selectedBackgroundColorHover: theme.colors.KALE_HOVER,
    selectedBorderColor: theme.colors.LIME,
  },
  cucumber: {
    backgroundColor: theme.colors.CUCUMBER,
    backgroundColorHover: theme.colors.CUCUMBER_HOVER,
    selectedBackgroundColor: theme.colors.CUCUMBER,
    selectedBackgroundColorHover: theme.colors.CUCUMBER_HOVER,
    selectedBorderColor: theme.colors.SPINACH,
  },
};

const palettePresetsForText: Record<RewardCardPalette, TextStyleProps> = {
  quinoa: StyleSheet.create({
    description: { color: theme.colors.DARK_KALE },
    extraDetails: { color: theme.colors.CHARCOAL },
  }),
  kale: StyleSheet.create({
    description: { color: theme.colors.LIME },
    extraDetails: { color: theme.colors.LIME },
  }),
  cucumber: StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    description: { color: theme.colors.KALE },
    // eslint-disable-next-line react-native/no-unused-styles
    extraDetails: { color: theme.colors.KALE },
  }),
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type RewardCardPalette = 'quinoa' | 'kale' | 'cucumber';

type RewardCardProps = Readonly<{
  id: string;
  imageSource: ImageSourcePropType;
  description: string;
  extraDetails?: string;
  palette?: RewardCardPalette;
  onPress?: (id: string) => void;
  isDisabled?: boolean;
}> &
  Omit<CardProps, 'onPress' | 'disabled' | CardStyleProp>;

type TextStyleProps = StyleSheet.NamedStyles<
  Readonly<{
    description: TextStyle;
    extraDetails: TextStyle;
  }>
>;

type CardStyleProps = Pick<CardProps, CardStyleProp>;

type CardStyleProp =
  | 'backgroundColor'
  | 'backgroundColorHover'
  | 'selectedBackgroundColor'
  | 'selectedBackgroundColorHover'
  | 'selectedBorderColor';

type CardProps = ComponentProps<typeof Card>;
